import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReportDomain } from '../types'
import { DOMAIN_ORDER, filterReports, groupByDomain } from './reports-hub-page/reports-hub.helpers'
import {
  DomainSection,
  EmptyState,
  ErrorView,
  FiltersBar,
  LoadingSpinner,
  PageHeader,
} from './reports-hub-page/reports-hub.parts'
import { useReportsData } from './reports-hub-page/use-reports-data'

export function ReportsHubPage(): ReactElement {
  const { t } = useTranslation('admin')
  const { reports, loading, error } = useReportsData()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDomain, setSelectedDomain] = useState<ReportDomain | 'all'>('all')

  if (loading) return <LoadingSpinner />
  if (error) {
    return (
      <ErrorView
        message={error}
        retryLabel={t('reportsHub.retry')}
        onRetry={() => {
          window.location.reload()
        }}
      />
    )
  }

  const filtered = filterReports({ reports, searchQuery, selectedDomain })
  const reportsByDomain = groupByDomain(filtered)
  const domainsWithReports = DOMAIN_ORDER.filter(
    (domain) => (reportsByDomain[domain]?.length ?? 0) > 0
  )
  const hasFilters = Boolean(searchQuery) || selectedDomain !== 'all'

  return (
    <div className="space-y-8">
      <PageHeader t={t} />
      <FiltersBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedDomain={selectedDomain}
        onDomainChange={setSelectedDomain}
        t={t}
      />
      {domainsWithReports.length === 0 ? (
        <EmptyState hasFilters={hasFilters} />
      ) : (
        <div className="space-y-10">
          {domainsWithReports.map((domain) => (
            <DomainSection
              key={domain}
              domain={domain}
              reports={reportsByDomain[domain] ?? []}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  )
}
